import React, { useState, useContext }  from 'react'
import { 
    Slide, 
    IconButton,
    Typography,
    CardHeader,
    CardContent,
    Box,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    Tooltip
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { reportIssuesList } from '../../util/Display';
import Constant from '../../util/constants';
import { StyledLoadingButton } from '../common/Styled';
import Token from '../../util/Token';
import HttpService from '../../services/HttpService';
import { Context } from '../../context';
import debounce from 'lodash/debounce'

const styles={
    reportHeader: {
        textAlign: 'start'
    },
    cardContent: {
        paddingTop: 0,
        textAlign: 'start'
    },
    toggleButtonGroup: (theme) => ({
        gap: '12px',
        width: '100%',
        mb: 2,
        '& .MuiToggleButtonGroup-grouped': {
            border: `1px solid ${theme.palette.grey[300]} !important`,
            borderRadius: '8px !important',
            textTransform: 'initial',
            color: theme.palette.text.primary,
            fontWeight: 500,
            margin: '0 !important',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                borderColor: `${theme.palette.primary.main} !important`,
                backgroundColor: 'transparent !important',
                color: theme.palette.primary.main,
            }
        },
        '& .MuiToggleButtonGroup-grouped.Mui-selected': {
            background: `${alpha(theme.palette.primary.main, 0.16)} !important`,
            color: `${theme.palette.primary.dark} !important`,
            borderColor: `${theme.palette.primary.main} !important`,
            borderWidth: '1px !important',
            fontWeight: 600,
            boxShadow: 'none !important',
            '&:hover': {
                background: `${alpha(theme.palette.primary.main, 0.24)} !important`,
            }
        }
    }),
    paperOthers: {
        padding: 2.5,
        bgcolor: 'rgba(0, 171, 85, 0.08)', // CMS-UI primary light alpha
        borderRadius: '12px',
        border: '1px solid rgba(0, 171, 85, 0.24)',
        mb: 2
    },
    textArea: {
        '& .MuiOutlinedInput-root': {
            bgcolor: '#ffffff',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(145, 158, 171, 0.32)', // Equivalent to theme.palette.grey[400] in Material
                borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-color)', 
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--app-color)',
                borderWidth: '2px',
            }
        },
        '& .MuiInputBase-input::placeholder': {
            opacity: 0.8,
            color: 'rgba(145, 158, 171, 1)'
        }
    }
}

const initialState = {
    issue: '',
    others: !1,
    message: '',
    loading: !1
}

function ReportIssue({ isShow, container, onReportClick, locationId, direction = 'left',chargerId=null }){
    const [issueState, setIssueState] = useState(initialState);
    const { message } = useContext(Context);
    
    const onExited = () => {
        onReportClick(!0, !1)();
    }

    const onIssueChecked = (e, value) => {
        setIssueState({
            ...issueState,
            issue: value,
            others: 'OTHER' === value ? !0 : !1
        })
    }

    const onSubmitReport = async () => {
        const { setMessageOption } = message;
        const { data } = Token.parsedToken();
        const othersIssues = document.getElementById('others-issue');
        setIssueState({ ...issueState, loading: !0 })
        const payload =chargerId!==null?{
            issue: issueState.issue,
                message: othersIssues?.value || '',
                user: data._id,
                chargerId,
                locationId
        }:{
            issue: issueState.issue,
                message: othersIssues?.value || '',
                user: data._id,
                locationId
        }
        const response = await HttpService(Constant.methods.REPORT_ISSUE,payload);
        if(response.status) {
            setIssueState(initialState);
            setMessageOption((prev) => ({
                ...prev,
                open: !0,
                message: Constant.message.ISSUE_REPORTED_SUCCESS
            }))
        }
    }

    return (
        <Slide 
        direction={direction}
        container={container} 
        in={isShow} 
        mountOnEnter 
        unmountOnExit 
        timeout={500} 
        onExited={onExited}
        >
            <Box>
                <CardHeader
                sx={styles.reportHeader}
                avatar={
                    <Tooltip title="Back" placement="top">
                        <IconButton aria-label="back to location detail" data-click-skip="true" onClick={onReportClick(!1, !1)}>
                            <ArrowBackOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                }
                title={<Typography variant="h6">{Constant.label.FLAG_AN_ISSUE}</Typography>}
                />
                <CardContent sx={styles.cardContent}>
                    <Typography variant="body1" mb={2}>{Constant.label.ISSUE_TITLE}</Typography>
                    <ToggleButtonGroup 
                    sx={styles.toggleButtonGroup} orientation='vertical' 
                    exclusive value={issueState.issue} onChange={onIssueChecked}>
                        {reportIssuesList.map(issue => (
                            <ToggleButton key={issue.id} disabled={issueState.loading} value={issue.value}>{issue.text}</ToggleButton>
                        ))}
                        <ToggleButton value='OTHER' disabled={issueState.loading}>{Constant.label.OTHERS}</ToggleButton>
                    </ToggleButtonGroup>
                    {issueState.others &&
                     <Paper sx={styles.paperOthers} component="div" elevation={0}>
                       <Typography variant="body2" mb={1.5} sx={{ color: 'text.secondary', fontWeight: 500 }}>{Constant.label.ISSUE_PARA}</Typography>
                       <TextField 
                       id="others-issue"
                       placeholder="Please describe the issue..."
                       multiline
                       name="message"
                       rows={4}
                       fullWidth
                       sx={styles.textArea}
                       />
                    </Paper>}
                    <StyledLoadingButton onClick={onSubmitReport} variant="contained" fullWidth loading={issueState.loading}>
                        {Constant.label.SUBMIT}
                    </StyledLoadingButton>
                </CardContent>
            </Box>
        </Slide>
    )
}

ReportIssue.defaultProps = {
    direction: 'left'
}

export default React.memo(ReportIssue);